<template>
  <a-modal :open="visible" title="裁剪图片" :width="640" :mask-closable="false" :destroy-on-close="true" :footer="null"
    @cancel="onCancel">
    <div class="cropper-wrap" @wheel.prevent="onWheel">
      <vue-cropper ref="cropperRef" class="cropper" :img="imageUrl" :output-size="1" :output-type="outputType || 'png'"
        :info="false" :can-scale="false" :auto-crop="true" :auto-crop-width="cropWidth" :auto-crop-height="cropHeight"
        :fixed="true" :fixed-number="[cropWidth, cropHeight]" :full="true" :fixed-box="false" :can-move="true"
        :can-move-box="true" :original="false" :center-box="true" :high="true" :max-img-size="2000" :enlarge="1"
        :mode="'cover'" :limit-min-size="[100, 100]" />
    </div>
    <div class="cropper-toolbar">
      <div class="toolbar-left">
        <a-button type="text" @click="rotateLeft" title="左旋">
          <RotateLeftOutlined />
        </a-button>
        <a-button type="text" @click="rotateRight" title="右旋">
          <RotateRightOutlined />
        </a-button>
        <a-button type="text" @click="changeScale(3)" title="放大">
          <ZoomInOutlined />
        </a-button>
        <a-button type="text" @click="changeScale(-3)" title="缩小">
          <ZoomOutOutlined />
        </a-button>
        <a-button type="text" @click="reset" title="重置">
          <ReloadOutlined />
        </a-button>
      </div>
      <div class="toolbar-right">
        <a-button class="cancel-btn" @click="onCancel">取消</a-button>
        <a-button type="primary" class="confirm-btn" @click="onConfirm">确定</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { VueCropper } from 'vue-cropper'
import 'vue-cropper/dist/index.css'
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue'

const props = defineProps<{
  visible: boolean
  imageUrl: string
  cropWidth: number
  cropHeight: number
  outputType?: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', file: File): void
  (e: 'cancel'): void
}>()

const cropperRef = ref<InstanceType<typeof VueCropper> | null>(null)

watch(() => props.visible, (val) => {
  if (val) {
    nextTick(() => {
      cropperRef.value?.reload()
    })
  }
})

const changeScale = (num: number) => {
  cropperRef.value?.changeScale(num)
}

const onWheel = (e: WheelEvent) => {
  // 触摸板双指缩放：deltaY 正负表示缩放方向
  // 乘以系数加大缩放幅度，提升灵敏度
  const delta = e.deltaY > 0 ? -3 : 3
  cropperRef.value?.changeScale(delta)
}

const rotateLeft = () => {
  cropperRef.value?.rotateLeft()
}

const rotateRight = () => {
  cropperRef.value?.rotateRight()
}

const reset = () => {
  cropperRef.value?.refresh()
}

const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

const onConfirm = () => {
  cropperRef.value?.getCropData((data: string) => {
    const isJpeg = props.outputType === 'jpeg' || props.outputType === 'jpg'
    const ext = isJpeg ? 'jpg' : 'png'
    const file = dataURLtoFile(data, `crop_${Date.now()}.${ext}`)
    emit('confirm', file)
    emit('update:visible', false)
  })
}

const onCancel = () => {
  emit('update:visible', false)
  emit('cancel')
}
</script>

<style scoped lang="less">
.cropper-wrap {
  width: 100%;
  height: 420px;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .cropper {
    width: 100%;
    height: 100%;
  }

  // 隐藏默认信息文字
  :deep(.vue-cropper-info) {
    display: none;
  }

  // 裁剪框样式
  :deep(.cropper-crop-box) {
    border: 2px solid #0f813d !important;
    ;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.65);
  }

  // 裁剪框内部视图框
  :deep(.cropper-view-box) {
    outline: 2px solid #0f813d;
    outline-offset: -1px;
  }

  :deep(.cropper-face) {
    background-color: transparent;
  }

  // 网格线
  :deep(.cropper-line) {
    background-color: rgba(15, 129, 61, 0.8);
  }

  // 八个角点（不同版本类名可能是 crop-point 或 cropper-point）
  :deep(.crop-point),
  :deep(.cropper-point) {
    width: 8px !important;
    height: 8px !important;
    margin: 0px !important;
    background-color: #0f813d !important;
    border: 1px solid #fff !important;
    box-sizing: content-box;
    border-radius: 50% !important;
    opacity: 1 !important;
  }

  // 按 vue-cropper 1.1.4 默认顺序：
  // point1 左上，point3 右上，point6 左下，point8 右下
  :deep(.crop-point.point1) {
    top: -5px !important;
    left: -5px !important;
  }

  :deep(.crop-point.point3) {
    top: -5px !important;
    right: -5px !important;
  }

  :deep(.crop-point.point6) {
    bottom: -5px !important;
    left: -5px !important;
  }

  :deep(.crop-point.point8) {
    bottom: -5px !important;
    right: -5px !important;
  }

  // point2 上中，point4 左中，point5 右中，point7 下中
  :deep(.crop-point.point2) {
    left: 50% !important;
    margin-left: -5px !important;
    top: -5px !important;
  }

  :deep(.crop-point.point4) {
    top: 50% !important;
    margin-top: -5px !important;
    left: -5px !important;
  }

  :deep(.crop-point.point5) {
    top: 50% !important;
    margin-top: -5px !important;
    right: -5px !important;
  }

  :deep(.crop-point.point7) {
    left: 50% !important;
    margin-left: -5px !important;
    bottom: -5px !important;
  }
}

.cropper-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;

    :deep(.ant-btn) {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      color: #666;

      &:hover {
        color: #0f813d;
        background: #f6ffed;
      }

      :deep(svg) {
        font-size: 18px;
      }
    }
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 12px;

    .cancel-btn {
      border-radius: 6px;
    }

    .confirm-btn {
      border-radius: 6px;
      background: #0f813d;
      border-color: #0f813d;

      &:hover {
        background: #0a6b31;
        border-color: #0a6b31;
      }
    }
  }
}
</style>
